import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { getUsersAsync } from "../../../slices/user";
import { useFormik } from "formik";
import PMCombobox from "../../utils/PMCombobox";
import { PMButton } from "../../utils/PMButton";
import { PMField } from "../../utils/PMField";

export const HseActionForm = ({
  hseEvent,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const itemState = useSelector((state) => state.users);

  const [responsibleUsers, setResponsibleUsers] = useState([]);

  useEffect(() => {
    // Load users for responsible person selection
    dispatch(getUsersAsync({ filters: [{ name: "EMPLOYEE", value: true }] }));
  }, [dispatch]);

  useEffect(() => {
    if (itemState?.data) {
      const users = itemState.data.map((user) => ({
        id: user.id,
        value: user.id,
        title: user.displayTitle,
        object: user,
      }));
      setResponsibleUsers(users);
    }
  }, [itemState?.data]);

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      action_type: "corrective", // corrective, preventive, immediate
      priority: "medium", // low, medium, high, critical
      responsible_user_id: "",
      due_date: "",
      status: "pending", // pending, in_progress, completed, cancelled
      cost_estimate: "",
      implementation_notes: "",
    },
    onSubmit: async (values, helpers) => {
      try {
        await onSubmit(values);
        helpers.setStatus({ success: true });
        helpers.setSubmitting(false);
      } catch (err) {
        console.error("Error creating HSE action:", err);
        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: err.message });
        helpers.setSubmitting(false);
      }
    },
  });

  const actionTypeOptions = [
    {
      id: "corrective",
      value: "corrective",
      title: t("Korrigerende tiltak"),
    },
    {
      id: "preventive",
      value: "preventive",
      title: t("Forebyggende tiltak"),
    },
    {
      id: "immediate",
      value: "immediate",
      title: t("Strakstiltak"),
    },
  ];

  const priorityOptions = [
    { id: "low", value: "low", title: t("Lav") },
    { id: "medium", value: "medium", title: t("Medium") },
    { id: "high", value: "high", title: t("Høy") },
    {
      id: "critical",
      value: "critical",
      title: t("Kritisk"),
    },
  ];

  const statusOptions = [
    { id: "pending", value: "pending", title: t("Venter") },
    {
      id: "in_progress",
      value: "in_progress",
      title: t("Under arbeid"),
    },
    {
      id: "completed",
      value: "completed",
      title: t("Fullført"),
    },
    {
      id: "cancelled",
      value: "cancelled",
      title: t("Kansellert"),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <h3 className="text-sm font-medium text-blue-800">
          {t("Hendelse")}: {hseEvent?.title}
        </h3>
        <p className="text-sm text-blue-700 mt-1">
          {t("Opprett tiltak for denne hendelsen")}
        </p>
      </div>

      <form onSubmit={formik.handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {/* Title */}
          <div className="sm:col-span-2">
            <PMField
              label={t("Tittel")}
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.title && formik.errors.title}
              required
            />
          </div>

          {/* Description */}
          <div className="sm:col-span-2">
            <PMField
              multiline
              label={t("Beskrivelse")}
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.description && formik.errors.description}
              rows={4}
              required
            />
          </div>

          {/* Action Type */}
          <div>
            <PMCombobox
              label={t("Type tiltak")}
              name="action_type"
              options={actionTypeOptions}
              value={formik.values.action_type}
              onChange={(value) => formik.setFieldValue("action_type", value)}
              error={formik.touched.action_type && formik.errors.action_type}
              required
            />
          </div>

          {/* Priority */}
          <div>
            <PMCombobox
              label={t("Prioritet")}
              name="priority"
              options={priorityOptions}
              value={formik.values.priority}
              onChange={(value) => formik.setFieldValue("priority", value)}
              error={formik.touched.priority && formik.errors.priority}
              required
            />
          </div>

          {/* Responsible User */}
          <div>
            <PMCombobox
              label={t("Ansvarlig person")}
              name="responsible_user_id"
              options={responsibleUsers}
              value={formik.values.responsible_user_id}
              onChange={(value) =>
                formik.setFieldValue("responsible_user_id", value)
              }
              error={
                formik.touched.responsible_user_id &&
                formik.errors.responsible_user_id
              }
              required
            />
          </div>

          {/* Status */}
          <div>
            <PMCombobox
              label={t("Status")}
              name="status"
              options={statusOptions}
              value={formik.values.status}
              onChange={(value) => formik.setFieldValue("status", value)}
              error={formik.touched.status && formik.errors.status}
              required
            />
          </div>

          {/* Due Date */}
          <div>
            <PMField
              label={t("Frist")}
              name="due_date"
              type="date"
              value={formik.values.due_date}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.due_date && formik.errors.due_date}
            />
          </div>

          {/* Cost Estimate */}
          <div>
            <PMField
              label={t("Kostnadsestimat")}
              name="cost_estimate"
              value={formik.values.cost_estimate}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.cost_estimate && formik.errors.cost_estimate
              }
              placeholder="NOK"
            />
          </div>
        </div>

        {/* Implementation Notes */}
        <div>
          <PMField
            multiline
            label={t("Implementeringsnotater")}
            name="implementation_notes"
            value={formik.values.implementation_notes}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.implementation_notes &&
              formik.errors.implementation_notes
            }
            rows={3}
          />
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <PMButton
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={loading}
          >
            {t("Avbryt")}
          </PMButton>
          <PMButton
            type="submit"
            variant="primary"
            loading={loading || formik.isSubmitting}
            disabled={loading || formik.isSubmitting}
          >
            {t("Opprett tiltak")}
          </PMButton>
        </div>
      </form>
    </div>
  );
};
